import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Mail, Check, Clock, AlertTriangle } from 'lucide-react';
import { useInvitations, useCreateInvitation } from '../hooks/useInvitations';

export function InvitationsPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const { data: invitations, isLoading } = useInvitations(eventId);
  const createInvitation = useCreateInvitation();

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'CLIENT' | 'VENDOR'>('CLIENT');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !eventId) return;

    createInvitation.mutate(
      { email, role, eventId },
      {
        onSuccess: () => {
          setEmail('');
        },
      },
    );
  };

  const pageTitle = eventId ? 'Event Invitations' : 'All Invitations';

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">{pageTitle}</h1>

      {/* Invite Form */}
      {eventId && (
        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-lg border p-4 flex flex-wrap gap-3 items-end"
          data-testid="invite-form"
        >
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
              className="w-full px-3 py-2 border rounded-md text-sm"
              data-testid="invite-email"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as 'CLIENT' | 'VENDOR')}
              className="px-3 py-2 border rounded-md text-sm"
              data-testid="invite-role"
            >
              <option value="CLIENT">Client</option>
              <option value="VENDOR">Vendor</option>
            </select>
          </div>
          <button
            type="submit"
            disabled={createInvitation.isPending}
            className="px-4 py-2 bg-primary-600 text-white rounded-md text-sm hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
            data-testid="invite-submit"
          >
            <Mail size={16} />
            {createInvitation.isPending ? 'Sending...' : 'Send Invitation'}
          </button>
          {createInvitation.isSuccess && (
            <span
              className="text-sm text-green-600 flex items-center gap-1"
              data-testid="invite-success"
            >
              <Check size={14} /> Invitation created (simulated)
            </span>
          )}
        </form>
      )}

      {/* Invitations List */}
      {isLoading ? (
        <p className="text-gray-500">Loading invitations...</p>
      ) : !invitations || invitations.length === 0 ? (
        <p className="text-gray-500" data-testid="no-invitations">
          No invitations yet.
        </p>
      ) : (
        <div className="bg-white rounded-lg border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left p-3">Email</th>
                <th className="text-left p-3">Role</th>
                <th className="text-left p-3">Event</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Created</th>
              </tr>
            </thead>
            <tbody>
              {invitations.map((inv) => (
                <tr key={inv.id} className="border-t" data-testid={`invitation-row-${inv.id}`}>
                  <td className="p-3">{inv.email}</td>
                  <td className="p-3">
                    <span
                      className={`text-xs px-2 py-0.5 rounded ${
                        inv.role === 'CLIENT'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-yellow-100 text-yellow-700'
                      }`}
                    >
                      {inv.role}
                    </span>
                  </td>
                  <td className="p-3">{inv.eventName || inv.eventId}</td>
                  <td className="p-3">
                    <span className="flex items-center gap-1">
                      {inv.status === 'ACCEPTED' && (
                        <>
                          <Check size={14} className="text-green-600" />
                          <span className="text-green-600">Accepted</span>
                        </>
                      )}
                      {inv.status === 'PENDING' && (
                        <>
                          <Clock size={14} className="text-yellow-600" />
                          <span className="text-yellow-600">Pending</span>
                        </>
                      )}
                      {inv.status === 'EXPIRED' && (
                        <>
                          <AlertTriangle size={14} className="text-red-600" />
                          <span className="text-red-600">Expired</span>
                        </>
                      )}
                    </span>
                  </td>
                  <td className="p-3 text-gray-500">
                    {new Date(inv.createdAt).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
